import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { BreadCrumb } from "../components/BreadCrumb";
import { TextField, MenuItem } from "@mui/material";
import { Button } from "@mui/material";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import {
  getBooks,
  getTeacherClasses,
  getClassRecords,
  updateClass,
  deleteClass,
  deleteClassRecord,
  getChapters,
  updateStudent,
} from "../services/api";
import { getUserData } from "../utils/userData";
import { classesForBook, classBelongsToBook, classRecordsBookParam } from "../data/examBlock";
import { isHsScheme25, categoryScore } from "../utils/correctionFormat";
import type {
  classDataProps,
  Record as ClassRecord,
  Chapter,
  FinalRecordsDataProps,
  StudentProps,
} from "../types/common";
import { filterHiddenChapters } from "../data/hiddenChapters";
import { pickClassWithChapterRecords } from "../utils/deepLinkClassPick";
import { ClassSettingModal } from "../components/ClassSettingModal";
import Swal from "sweetalert2";
import { downloadScoreExcel, downloadReportZip } from "../utils/classReportDownload";
import { CopyModal } from "../components/CopyModal";
import { getStudents } from "../services/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import DescriptionIcon from "@mui/icons-material/Description";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { timeProcess } from "../utils/timeProcess";
import { StudentSettingModal } from "../components/StudentSettingModal";
import TopicDesc from "../components/TopicDesc";
import {
  deleteBatchResult,
  fetchBatchClassesByCreator,
  fetchBatchResultsByClass,
  reopenBatchResult,
} from "../services/batchApi";
import {
  classifyBatchStatus,
  findBatchClassId,
  groupPendingRowsByChapter,
  hasGradingRows,
  markBatchResultReopened,
  removeBatchResult,
  type BatchResultLike,
  type PendingBatchRow,
} from "../utils/batchGradingStatus";
import {
  canDeleteClassRecord,
  classRecordDeleteSteps,
  classifyRecordDeleteFailure,
} from "../utils/classRecordDelete";
import { buildFeedbackSiblings } from "../utils/feedbackSiblingNav";
import {
  canRegradeBrokenRecord,
  extractTotalScore,
  parseCorrectingResult,
  splitRecordsByParseError,
} from "../utils/correctingResultGuard";
import {
  cancelRegrading,
  computeNextRegradingState,
  startRegrading,
  type RegradingState,
} from "../utils/regradingConvergence";

export const CorrectingRecords = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [chaptersData, setChaptersData] = useState<Chapter[]>([]);
  const [selectBook, setSelectBook] = useState("");
  const [selectedChapter, setSelectedChapter] = useState("");
  const [allClassData, setAllClassData] = useState([]);
  const [showQuestionPopup, setShowQuestionPopup] = useState(false);

  // 117901：下拉選單隱藏已下架的題目；chaptersData 本體保持完整，
  // 讓老師的查詢深連結仍查得到標題、題目內容與紀錄
  const chapterOptions = useMemo(() => {
    const visible = filterHiddenChapters(chaptersData);
    const current = chaptersData.find((ch) => ch._id === selectedChapter);
    return current && !visible.includes(current) ? [...visible, current] : visible;
  }, [chaptersData, selectedChapter]);
  
  // 從 URL 參數獲取篩選條件
  const filterBookId = searchParams.get('bookId');
  const filterChapterId = searchParams.get('chapterId');
  const filterChapterName = searchParams.get('chapterName');

  // 檢查教師身份
  useEffect(() => {
    const checkTeacherAccess = () => {
      const userDataString = localStorage.getItem("oidc.user:https://id.hle.com.tw:js");
      
      if (!userDataString) {
        navigate("/");
        return;
      }

      try {
        const userData = JSON.parse(userDataString);
        const userRole = userData.profile?.role || userData.profile?.['custom:role'] || "學生";
        
        if (userRole !== "老師") {
          navigate("/student-home");
        }
      } catch (error) {
        console.error("解析用戶資料失敗:", error);
        navigate("/");
      }
    };

    checkTeacherAccess();
  }, [navigate]);
  const [classData, setClassData] = useState<classDataProps[]>([]);
  const [classRecordsData, setClassRecordsData] = useState([]);
  const [selectClass, setSelectClass] = useState<classDataProps | null>(null);
  // 目前選中的班,給非同步回應判斷「這筆結果還是不是給現在這個班的」用(kanban 1001506)
  const selectClassRef = useRef<classDataProps | null>(null);
  useEffect(() => {
    selectClassRef.current = selectClass;
  }, [selectClass]);
  const [isClassSetOpen, setIsClassSetOpen] = useState(false);
  const [isCopyOpen, setIsCopyOpen] = useState(false);
  const [finalRecordsData, setFinalRecordsData] = useState<
    FinalRecordsDataProps[]
  >([]);
  const [isStudentModalOpen, setIsStudentModalOpen] = useState(false);
  const [studentsData, setStudentsData] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isRecordsLoading, setIsRecordsLoading] = useState(false);
  const [isShowTabs, setIsShowTabs] = useState(true);
  const [originTabsWidth, setOriginTabsWidth] = useState(0);
  const [canEditStudent, setCanEditStudent] = useState(false);
  const [recordFilter, setRecordFilter] = useState<'all' | 'best' | 'first'>('all');
  // 下載成績表 Excel / 學生成績單 zip(kanban 107190,依章節)
  const [downloadingExcel, setDownloadingExcel] = useState<string | null>(null);
  const [downloadingZip, setDownloadingZip] = useState<string | null>(null);
  // 批次批改中/失敗(batch 後端 batch-results;完成前不存在 classrecord,kanban 106957)。
  // 獨立 state,絕不併入 finalRecordsData.records —— Excel 下載/成績單 zip/
  // 最佳/第一次篩選全吃那個陣列,塞進去就污染下載與統計。
  const [batchResults, setBatchResults] = useState<BatchResultLike[]>([]);
  const batchReqSeqRef = useRef(0);
  // 高中班名 → batch classID 快取:切班解一次,輪詢 tick 只打 batch-results。
  // 只快取「有解到」的(null 不存):班還沒有 batch 容器時之後送單才會建,
  // 快取 miss 會把新容器永遠擋在外面
  const batchClassIdCacheRef = useRef<Record<string, string>>({});
  // 「重新批改」送出中的 resultID(防連點,同國中 batch-records 的 reopeningId)
  const [reopeningId, setReopeningId] = useState<string | null>(null);
  // 「移除」送出中的 resultID(防連點)
  const [removingId, setRemovingId] = useState<string | null>(null);
  // 「刪除已完成紀錄」送出中的 classrecord _id(防連點;與上方兩個 batch-results
  // 的 state 分開 —— 上下兩表是不同資料源、不同 row 型別,共用一個 state 會互鎖)
  const [deletingRecordId, setDeletingRecordId] = useState<string | null>(null);
  // 壞 JSON 紀錄已按「重新批改」的 classrecord _id → 收斂階段(kanban 1000412)。
  // reopen 後的批次列因跨來源去重(_id 已在 classRecordIds)永遠不會出現在
  // pendingRows,「批改中」狀態只能由 parseError 列靠這個 state 自行呈現;
  // 同時它也是輪詢喚醒條件(見 shouldPollBatch)。每個 id 的階段機
  // (requeued/running/收斂出口)見 utils/regradingConvergence.ts 檔頭。
  //
  // 已知且接受的限制:這是元件 local state,切班/離頁重回即遺失 —— 該筆會
  // 顯示「批改失敗」,實際上 worker 可能仍在重跑(此時按「刪除」有被 worker
  // upsert 復活的縫)。收不掉的原因:reopen 後的 batch 列被跨來源去重擋住
  // (上一段),server 端也沒有任何可查的「regrading 中」旗標,重進頁面無從
  // 重建。後果僅止於顯示與那道刪除縫,重批結果本身不受影響,故接受不修。
  const [regradingIds, setRegradingIds] = useState<RegradingState>(new Map());
  // reopen PUT 在途中的 id:收斂 effect 一律跳過,PUT 結束(成功或失敗)才移出。
  // 擋的是「PUT 在途期間落地的輪詢舊快照(還載著 reopen 前狀態)把剛樂觀加入
  // 的 id 提早清掉」—— 批改中閃回批改失敗、之後 requeue 成功卻沒人把 id 加回。
  // 單把 batchReqSeqRef++ 提前擋不住 await 期間才起跑的新輪詢 tick,ref 才擋得住
  const regradeInFlightRef = useRef<Set<string>>(new Set());

  const handleDownloadExcel = async (chapterName: string, records: ClassRecord[]) => {
    setDownloadingExcel(chapterName);
    try {
      await downloadScoreExcel({
        records,
        className: selectClass?.className || "班級",
        chapterName,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "下載失敗",
        text: err instanceof Error ? err.message : "產生 Excel 時發生錯誤",
      });
    } finally {
      setDownloadingExcel(null);
    }
  };

  const handleDownloadReportZip = async (chapterName: string, records: ClassRecord[]) => {
    setDownloadingZip(chapterName);
    try {
      const { merged } = await downloadReportZip({
        records,
        className: selectClass?.className || "班級",
        chapterName,
      });
      if (!merged) {
        // 單人檔已下載，只有全班合併檔（kanban 1005602）沒產生
        Swal.fire({
          icon: "warning",
          title: "全班合併檔未產生",
          text: "已下載每位學生的 Word 檔，但全班合併檔產生失敗，zip 內沒有「00_合併檔」。",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "下載失敗",
        text: err instanceof Error ? err.message : "產生成績單時發生錯誤",
      });
    } finally {
      setDownloadingZip(null);
    }
  };

  // 下載鈕照設計稿：左=白底框線+下載 icon、右=深藍實底+文件 icon(kanban 107190)
  const renderDownloadButtons = (chapterName: string, records: ClassRecord[]) => (
    <div className="flex flex-wrap items-center gap-2 shrink-0">
      <button
        onClick={() => handleDownloadExcel(chapterName, records)}
        disabled={downloadingExcel === chapterName}
        className="flex items-center gap-1 px-2.5 py-[3px] text-[13px] leading-[1.75] font-medium bg-white text-[#1B3A6B] border border-[#1B3A6B] rounded hover:bg-[#f2f5fa] transition-colors disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-wait"
      >
        <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
        {downloadingExcel === chapterName ? "產生中..." : "下載成績表 Excel"}
      </button>
      <button
        onClick={() => handleDownloadReportZip(chapterName, records)}
        disabled={downloadingZip === chapterName}
        className="flex items-center gap-1 px-2.5 py-[3px] text-[13px] leading-[1.75] font-medium bg-[#1B3A6B] text-white rounded hover:bg-[#16305a] transition-colors disabled:bg-gray-400 disabled:cursor-wait"
      >
        <DescriptionIcon sx={{ fontSize: 16 }} />
        {downloadingZip === chapterName ? "產生中..." : "下載學生成績單"}
      </button>
    </div>
  );

  // 只顯示單一章節有紀錄時，下載鈕放「班級資料」列右側（設計稿位置）；
  // 多章節同時顯示時退回各表格上方，避免下載對象混淆
  const chaptersWithRecords = finalRecordsData.filter(
    (ch) => ch.records.length > 0
  );
  const singleDownloadTarget =
    chaptersWithRecords.length === 1 ? chaptersWithRecords[0] : null;

  const { name, email, sub } = getUserData();
  const tabRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLDivElement>(null);
  const mainContainerRef = useRef<HTMLDivElement>(null);

  // 拉 batch 端「批改中/失敗」:高中班級碼 → batch classID → batch-results。
  // 呼叫端一律不 await,不得阻塞 classrecord 主載入;任一步失敗或對不出
  // batch 班級(B8)都靜默缺席,原有畫面照常(B3)。
  // seq 擋切班競態:舊班的慢回應不得蓋掉新班資料(B6)。
  const loadBatchResults = useCallback(
    async (target: { className: string; classCode: string }) => {
      const seq = ++batchReqSeqRef.current;
      try {
        // 快取鍵要含書籍與班級碼:批次班級以 classCode + bookID 為唯一鍵(kanban 106957),
        // 只用班名當鍵,換書會拿到上一本書的 batch classID、刪班重建同名班會沿用舊班的
        const cacheKey = `${selectBook} ${target.classCode} ${target.className}`;
        let batchClassId: string | null =
          batchClassIdCacheRef.current[cacheKey] ?? null;
        if (!batchClassId) {
          // 帶當前書籍:批次紀錄屬於送單當下那本書,不跨書沿用
          const batchClasses = await fetchBatchClassesByCreator(sub, selectBook);
          batchClassId = findBatchClassId(batchClasses, target);
          if (batchClassId) {
            batchClassIdCacheRef.current[cacheKey] = batchClassId;
          }
        }
        const results = batchClassId
          ? await fetchBatchResultsByClass(batchClassId)
          : [];
        if (seq === batchReqSeqRef.current) {
          setBatchResults(Array.isArray(results) ? results : []);
        }
      } catch {
        if (seq === batchReqSeqRef.current) setBatchResults([]);
      }
    },
    [sub, selectBook]
  );

  // 正式紀錄的 _id 集合,跨來源去重用:classrecord 的 _id 就是 batch-results 的
  // batchResultID(同一筆批次批改,見 handleDeleteClassRecord)。後端「先寫回
  // classrecord → 才標 finish」,窗口期不去重會同一學生顯示兩列(kanban 106957)
  const classRecordIds = useMemo(
    () =>
      new Set(
        (classRecordsData as ClassRecord[])
          .map((record) => record?._id)
          .filter((id): id is string => typeof id === "string" && id !== "")
      ),
    [classRecordsData]
  );

  // 以 compositionTitle 對映章節(送單時寫入的就是章節名,見 batchApi submitBatch)。
  // 對整本書的章節做;單選題目時的顯示範圍由 finalRecordsData 決定。
  const { byChapter: batchPendingByChapter, unmatchedTitles } = useMemo(
    () =>
      groupPendingRowsByChapter(
        batchResults,
        chaptersData.map((ch) => ch.chapter),
        classRecordIds
      ),
    [batchResults, chaptersData, classRecordIds]
  );

  // B4:compositionTitle 對不上章節 → 不顯示不炸,只 warn(同名 batch 班可能
  // 跨書共用容器,別書的題目也會落到這裡,所以措辭不咬死是改名)。
  // 以 join 後字串當依存,輪詢每 10 秒換新陣列也不會重複洗版。
  const unmatchedKey = unmatchedTitles.join("、");
  useEffect(() => {
    if (unmatchedKey) {
      console.warn(
        "批次批改題目對不上此書任何章節（可能屬於其他書，或章節已改名），暫不顯示：",
        unmatchedKey
      );
    }
  }, [unmatchedKey]);

  // 壞 JSON 且仍解析失敗的 classrecord _id 集合(kanban 1000412):
  // regradingIds 的收斂依據 —— 重批完成後 worker 以同一個 _id upsert 覆寫,
  // 該筆重新解析成功就會離開這個集合。不用 finalRecordsData.brokenRecords
  // (只涵蓋當前顯示章節,切章節會誤判「修好了」),直接對整份 classRecordsData
  // 重新判定;splitRecordsByParseError 就地轉換過的好筆已是物件,不會誤中
  const brokenRecordIds = useMemo(() => {
    const ids = new Set<string>();
    for (const record of classRecordsData as ClassRecord[]) {
      if (
        typeof record?.correctingResult === "string" &&
        !parseCorrectingResult(record.correctingResult).ok &&
        typeof record._id === "string" &&
        record._id !== ""
      ) {
        ids.add(record._id);
      }
    }
    return ids;
  }, [classRecordsData]);

  // batchResultID → UI 狀態:regradingIds 收斂狀態機的輸入之一。
  // failed/prohibited 是終端(壞資料不會被覆寫,留著就是 10 秒一次的無限輪詢,
  // B7 同理);finish 交給階段機判「重批後仍壞」(見 regradingConvergence.ts)
  const batchStatusById = useMemo(() => {
    const map = new Map<string, ReturnType<typeof classifyBatchStatus>>();
    for (const item of batchResults) {
      if (item?.batchResultID) {
        map.set(item.batchResultID, classifyBatchStatus(item?.status));
      }
    }
    return map;
  }, [batchResults]);

  // regradingIds 收斂:出口與階段機全在 computeNextRegradingState(純函式,
  // 測試釘死各序列)。移除後該列退回「批改失敗」可再按或刪除;全空時輪詢
  // 自然停下。regradeInFlightRef 是 ref 不進依存 —— 在途保護只要求「effect
  // 跑的當下」跳過在途 id,PUT 結束後本來就有輪詢回應觸發下一次收斂
  useEffect(() => {
    setRegradingIds((prev) =>
      computeNextRegradingState(prev, {
        brokenIds: brokenRecordIds,
        batchStatuses: batchStatusById,
        inFlightIds: regradeInFlightRef.current,
      })
    );
  }, [brokenRecordIds, batchStatusById]);

  // 輪詢:僅「有對上章節的批改中列」才每 10 秒(全 unmatched 顯示不出來,
  // 不驅動輪詢);failed/prohibited 是終端狀態,絕不算進條件,否則永遠輪不停(B7)。
  // 切班/切書/卸載由 cleanup 清 timer(B6)。
  // 每輪同時靜默刷新 classrecord:批改完成的紀錄由 batch 後端寫回 classrecord,
  // 不刷的話「批改中」列消失、正式紀錄卻要手動重整才出現(A5)。
  // 壞 JSON 列按了「重新批改」(regradingIds 非空)也要輪:那筆因跨來源去重
  // 不在 pendingRows,hasGradingRows 看不到它;classrecord 與 batch-results
  // 每輪都刷,覆寫完成後畫面才會自動更新(kanban 1000412)
  const shouldPollBatch =
    hasGradingRows(batchPendingByChapter) || regradingIds.size > 0;
  useEffect(() => {
    if (!shouldPollBatch || !selectClass) return;
    let cancelled = false;
    const { classCode } = selectClass;
    const bookParam = classRecordsBookParam(selectBook);
    const timer = setInterval(() => {
      loadBatchResults(selectClass);
      getClassRecords(classCode, bookParam)
        .then((res) => {
          if (!cancelled) setClassRecordsData(res.data);
        })
        .catch(() => {}); // 靜默失敗,下一輪再試(B3)
    }, 10000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [shouldPollBatch, selectClass, selectBook, loadBatchResults]);

  // 輪詢條件 true→false(批改剛全數結束):延遲補一次正式紀錄刷新(A5)。
  // 後端順序是「寫回 classrecord → 才標 finish」,但同一輪的 classrecord 與
  // batch-results 請求是並行送出的,回應交錯時最後一批可能還沒進畫面,這裡兜底。
  const prevPollRef = useRef(false);
  useEffect(() => {
    const wasPolling = prevPollRef.current;
    prevPollRef.current = shouldPollBatch;
    if (!wasPolling || shouldPollBatch || !selectClass) return;
    let cancelled = false;
    const { classCode } = selectClass;
    const bookParam = classRecordsBookParam(selectBook);
    const timer = setTimeout(() => {
      getClassRecords(classCode, bookParam)
        .then((res) => {
          if (!cancelled) setClassRecordsData(res.data);
        })
        .catch(() => {});
    }, 3000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [shouldPollBatch, selectClass, selectBook]);

  // D1:批改中/失敗列只在「所有記錄」模式顯示;最佳/第一次不顯示、不參與計算
  const pendingRowsFor = (chapterName: string): PendingBatchRow[] =>
    recordFilter === "all" ? batchPendingByChapter[chapterName] ?? [] : [];

  // 批改失敗(壞 JSON)列(kanban 1000412)比照 D1:僅「所有記錄」模式顯示;
  // 最佳/第一次的分組計算在 splitRecordsByParseError 就把 parseError 筆排除了
  const brokenRowsFor = (chapter: FinalRecordsDataProps): ClassRecord[] =>
    recordFilter === "all" ? chapter.brokenRecords : [];

  // 重新批改(僅 failed;prohibited 是內容被 AI 擋,重跑結果一樣,同國中不給):
  // PUT 改回待處理後,本地立即把該筆轉回 open → 該列馬上顯示「批改中」、
  // 輪詢條件轉 true 接手,不等下一輪
  const handleReopenBatchResult = async (row: PendingBatchRow) => {
    if (!row.resultID || reopeningId || removingId) return; // 防連點:送出中一律擋
    setReopeningId(row.resultID);
    try {
      await reopenBatchResult(row.resultID);
      // 作廢在途輪詢讀取:還載著舊 failed 狀態的回應不得蓋掉樂觀更新
      batchReqSeqRef.current++;
      setBatchResults((prev) => markBatchResultReopened(prev, row.resultID));
    } catch {
      Swal.fire({
        icon: "error",
        title: "重新批改失敗",
        text: "請稍後再試",
      });
    } finally {
      setReopeningId(null);
    }
  };

  // 壞 JSON 紀錄的「重新批改」(kanban 1000412,僅批次來源):classrecord 的 _id
  // 就是 batch-results 的 batchResultID,同一支 PUT 把該筆與訂單設回 open 並
  // requeue;重跑完成後 worker 以同一個 _id upsert 覆寫壞資料,該列自動變回
  // 正常紀錄。樂觀更新:先進 regradingIds(該列立即顯示「批改中」,兼防連點),
  // 失敗再退回。之後的收斂(含「重批後仍壞」的終端與保底)由
  // computeNextRegradingState 的階段機接手 —— 不在這裡動 batchResults:
  // requeued→running 必須由「輪詢真的看到 open/running」驅動,本地先標 open
  // 會讓階段機提早升級,失去「finish 先到、覆寫後的 classrecord 慢一拍」的防護;
  // reopen 前殘留的 finish/終端舊快照,requeued 階段與在途集合都不吃,不需本地清
  const handleRegradeBrokenRecord = async (record: ClassRecord) => {
    // UI 已依 canRegradeBrokenRecord 隱藏按鈕,這裡再守一次:非批次來源/_id
    // 異常的紀錄絕不能拿去打 batch API(誤中會 404 或改到別筆)
    if (!canRegradeBrokenRecord(record) || deletingRecordId) return;
    if (regradingIds.has(record._id)) return; // 防連點:送出中/批改中一律擋
    const recordId = record._id;
    regradeInFlightRef.current.add(recordId); // 在途保護:收斂 effect 跳過此 id
    setRegradingIds((prev) => startRegrading(prev, recordId));
    try {
      await reopenBatchResult(recordId);
      // 作廢在途輪詢讀取:PUT 前起跑、還載著 reopen 前狀態的回應不得落地
      batchReqSeqRef.current++;
    } catch (error) {
      // 4xx/5xx(如 batchResult 已被清):退回「批改失敗」,可重按或改用刪除
      console.error("❌ 壞資料紀錄重新批改失敗:", error);
      setRegradingIds((prev) => cancelRegrading(prev, recordId));
      Swal.fire({
        icon: "error",
        title: "重新批改失敗",
        text: "請稍後再試，或刪除此筆紀錄後重新上傳。",
      });
    } finally {
      regradeInFlightRef.current.delete(recordId);
    }
  };

  // 移除失敗/無法批改列(DELETE batch-results,同國中「刪除紀錄」)。
  // 批改中不給:worker 正在跑,刪單會留下不一致狀態。
  // 只動獨立 state,classrecord 正式紀錄完全不受影響。
  const handleRemoveBatchResult = async (row: PendingBatchRow) => {
    if (!row.resultID || removingId || reopeningId) return; // 防連點:送出中一律擋
    const confirm = await Swal.fire({
      icon: "warning",
      title: "移除此筆紀錄？",
      text: "移除後無法復原。",
      showCancelButton: true,
      confirmButtonText: "移除",
      cancelButtonText: "取消",
      confirmButtonColor: "#d33",
      reverseButtons: true,
    });
    if (!confirm.isConfirmed) return;
    setRemovingId(row.resultID);
    try {
      await deleteBatchResult(row.resultID);
      // 作廢在途輪詢讀取:還載著已刪那筆的回應不得把它蓋回畫面
      batchReqSeqRef.current++;
      setBatchResults((prev) => removeBatchResult(prev, row.resultID));
    } catch {
      Swal.fire({
        icon: "error",
        title: "移除失敗",
        text: "請稍後再試",
      });
    } finally {
      setRemovingId(null);
    }
  };

  // 刪除完成紀錄後的刷新:重拉 classrecord(該列消失)。
  // batch-results 也順手重拉 —— 兩表是不同資料源,同一支輪詢/切班用的 loader,
  // 保證「刪掉的那筆」不會以任何形式殘留在畫面上。
  const refreshRecordsAfterDelete = useCallback(async () => {
    if (!selectClass) return;
    try {
      const res = await getClassRecords(
        selectClass.classCode,
        classRecordsBookParam(selectBook)
      );
      setClassRecordsData(res.data);
    } catch (error) {
      console.error("❌ 刪除後重新載入班級記錄失敗:", error);
    }
    loadBatchResults(selectClass);
  }, [selectClass, selectBook, loadBatchResults]);

  // 刪除「已完成」的 classrecord(kanban 106957 補做,沿用國中批次紀錄頁的動線)。
  // 老師要重新批改某位學生時得先刪舊紀錄:批次後端以 (classID, compositionID, seatNo)
  // 判定重複,舊紀錄還在就靜默忽略重傳,老師會覺得系統毫無反應。
  // 第 1 項放寬:不分來源都給刪(老師批次上傳、學生自行上傳、老師本人自行上傳),
  // 但走哪條刪法要分流 —— 批次紀錄兩段(classrecord + batch-results),自行上傳
  // 一段(只 classrecord,_id 不是 batchResultID,不可打 batch API)。
  // 順序固定「先 classrecord 後 batch-results」:classrecord 是老師看得到的那一列,
  // 也是唯一會擋下操作(403)的一關,先過它才動批次端。
  const handleDeleteClassRecord = async (record: ClassRecord) => {
    // 防連點;僅放行「已完成(非批改中)」的 classrecord,與顯示條件一致
    if (!record._id || deletingRecordId) return;
    if (!canDeleteClassRecord(record)) return;
    const steps = classRecordDeleteSteps(record.studentID);
    const confirm = await Swal.fire({
      icon: "warning",
      title: "刪除此筆紀錄？",
      text: "刪除後無法復原。",
      showCancelButton: true,
      confirmButtonText: "刪除",
      cancelButtonText: "取消",
      confirmButtonColor: "#d33",
      reverseButtons: true,
    });
    if (!confirm.isConfirmed) return;
    setDeletingRecordId(record._id);
    try {
      try {
        await deleteClassRecord(record._id, sub);
      } catch (error) {
        const failure = classifyRecordDeleteFailure(error);
        // 403(非本班老師/非 batch_ 紀錄)與其他錯誤:紀錄還在,一律中止 ——
        // 這時去刪 batch-results 會變成「畫面上還看得到、卻能重複上傳」的不一致
        if (failure !== "gone") {
          console.error("❌ 刪除批改紀錄失敗:", error);
          Swal.fire({
            icon: "error",
            title: "刪除失敗",
            text:
              failure === "forbidden"
                ? "您沒有權限刪除這筆紀錄。"
                : "請稍後再試",
          });
          return;
        }
        // 404:紀錄已不存在(連點兩次、或已被他人刪除)。目的已達成,照常往下走
        console.warn("批改紀錄已不存在,視為刪除成功:", record._id);
      }
      // 第二段僅批次紀錄要走:studentID `batch_` 開頭時,_id 同時是 batch-results
      // 的 batchResultID,這筆不清掉批次端仍認得這位學生已交過,重新上傳照樣被擋。
      // 學生／老師自行上傳的紀錄沒有對應 batchResult,_id 不是 batchResultID,
      // 這裡跳過,絕不可誤打(會 404 或誤刪別筆)。
      if (steps.includes("batchResult")) {
        try {
          await deleteBatchResult(record._id);
        } catch (error) {
          // 紀錄已刪、畫面那列已消失 —— 不可讓整個操作看起來失敗。
          // 殘留只影響「能否重新上傳」,據實提示但不擋流程。
          console.warn("批改紀錄已刪除,但批次資料清除失敗:", error);
          Swal.fire({
            icon: "warning",
            title: "紀錄已刪除",
            text: "但批次系統的舊資料未清除完成，若重新上傳這位學生沒有反應，請稍後再試或聯繫客服。",
          });
        }
      }
      await refreshRecordsAfterDelete();
    } finally {
      setDeletingRecordId(null);
    }
  };

  // 批改中/失敗徽章:黃點 animate-pulse/紅字/橘字仿國中 batch-records;
  // 失敗類以 title 附說明。檢視鈕一律不給;操作僅 failed 有「重新批改」(同國中)。
  const renderBatchStatusBadge = (row: PendingBatchRow) => {
    if (row.status === "grading") {
      return (
        <span className="inline-flex items-center gap-1.5 text-amber-600">
          <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
          批改中
        </span>
      );
    }
    if (row.status === "failed") {
      return (
        <span
          className="inline-flex items-center gap-1.5 text-red-600 cursor-help"
          title="AI批改發生異常，請重新進行批改。"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          批改失敗
        </span>
      );
    }
    return (
      <span
        className="inline-flex items-center gap-1.5 text-orange-600 cursor-help"
        title="因第三方 AI 服務的安全過濾機制，偵測到此篇作文內容可能涉及敏感題材，暫時無法進行辨識與批改。此篇作文請改以人工方式批閱。"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
        </svg>
        無法批改
      </span>
    );
  };

  // 壞 JSON 紀錄徽章(kanban 1000412):correctingResult 是壞 JSON 的完成紀錄,
  // 顯示「批改失敗」,樣式沿用上方批次失敗那套(紅字+驚嘆號 SVG+title 說明);
  // 已按「重新批改」送出後改顯示「批改中」(黃點 animate-pulse,同批次列)
  const renderParseErrorBadge = (isRegrading: boolean) =>
    isRegrading ? (
      <span className="inline-flex items-center gap-1.5 text-amber-600">
        <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
        批改中
      </span>
    ) : (
      <span
        className="inline-flex items-center gap-1.5 text-red-600 cursor-help"
        title="AI 回傳的批改結果格式錯誤，可重新批改或刪除後重新上傳"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        批改失敗
      </span>
    );

  const handleSetting = async () => {
    const studentsData = await getStudents(selectClass?.classCode || "");
    setStudentsData(studentsData.data);
    if (studentsData.data.length > 0) {
      setCanEditStudent(true);
    } else {
      setCanEditStudent(false);
    }
    setIsClassSetOpen(true);
  };

  const handleCopy = () => {
    setIsCopyOpen(true);
  };

  const handleClassSwitch = async (classData: classDataProps) => {
    setSelectClass(classData);
    setIsRecordsLoading(true);
    // 切班先清舊班的批改中列再非同步補新班的(不 await,不阻塞 classrecord)
    setBatchResults([]);
    loadBatchResults(classData);
    try {
      const classRecords = await getClassRecords(classData.classCode, classRecordsBookParam(selectBook));
      setClassRecordsData(classRecords.data);
    } catch (error) {
      console.error("❌ 載入班級記錄失敗:", error);
      setClassRecordsData([]);
    } finally {
      setIsRecordsLoading(false);
    }
  };

  const handleClassNameChange = async (className: string) => {
    try {
      if (selectClass) {
        await updateClass(
          selectClass._id,
          sub,
          email,
          name,
          className,
          selectClass.classCode
        );
        Swal.fire({
          icon: "success",
          title: "變更成功",
          timer: 1500,
          showConfirmButton: false,
        });
        const classes = await getTeacherClasses(sub);
        setAllClassData(classes.data);
        setIsClassSetOpen(false);
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: "變更失敗",
        timer: 1500,
        showConfirmButton: false,
      });
    }
  };

  // 刪除班級
  const handleDeleteClass = async () => {
    if (!selectClass) return;

    const result = await Swal.fire({
      title: "刪除班級確認",
      html: `
        <div style="text-align: center; line-height: 1.8;">
          <p>您確定要刪除此班級嗎？</p>
          <p>此動作無法復原，</p>
          <p>該班級所有的批改與測驗紀錄將永久移除，</p>
          <p>您將無法再檢視或下載任何相關資料。</p>
          <p style="margin-top: 16px;">為了確保您已了解風險，</p>
          <p>請勾選「確認執行刪除」以繼續。</p>
        </div>
      `,
      icon: "warning",
      input: "checkbox",
      inputPlaceholder: "確認執行刪除",
      showCancelButton: true,
      confirmButtonText: "確認刪除",
      cancelButtonText: "取消",
      confirmButtonColor: "#6e7881",
      reverseButtons: true,
      didOpen: () => {
        const confirmBtn = Swal.getConfirmButton();
        const checkbox = Swal.getInput();
        if (checkbox && confirmBtn) {
          checkbox.addEventListener("change", () => {
            if (checkbox.checked) {
              confirmBtn.style.backgroundColor = "#d33";
            } else {
              confirmBtn.style.backgroundColor = "#6e7881";
            }
          });
        }
      },
      inputValidator: (result) => {
        if (!result) {
          return "請勾選確認執行刪除才能繼續";
        }
        return null;
      },
    });

    if (result.isConfirmed) {
      try {
        await deleteClass(selectClass.classCode);

        // 更新本地狀態
        const updatedClasses = allClassData.filter(
          (cls: classDataProps) => cls._id !== selectClass._id
        );
        setAllClassData(updatedClasses);
        setSelectClass(null);
        setIsClassSetOpen(false);

        Swal.fire({
          icon: "success",
          title: "班級已刪除",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("刪除班級失敗:", error);
        Swal.fire({
          icon: "error",
          title: "刪除失敗",
          text: "請稍後再試",
        });
      }
    }
  };

  const handleViewRecord = (
    record: ClassRecord,
    chapterRecords: ClassRecord[] = []
  ) => {
    record = {
      ...record,
      correctingResult: JSON.stringify(record.correctingResult),
      className: selectClass?.className,
    };
    
    // 保存當前的選擇狀態，以便返回時恢復
    const teacherState = {
      selectBook,
      selectedChapter,
      selectedClass: selectClass,
      recordFilter
    };
    sessionStorage.setItem("teacherState", JSON.stringify(teacherState));
    
    // 設置 workflowResult 到 sessionStorage，以便 Feedback 頁面使用
    const workflowResult = {
      // 紀錄 _id 與班級碼：老師在結果頁編輯批改內容時呼叫後端更新用(kanban 106732)
      _id: record._id,
      classCode: selectClass?.classCode || "",
      correctingResult: record.correctingResult,
      bookID: record.bookID,
      chapterID: record.chapterID,
      studentName: record.studentName,
      studentSeatNo: record.studentSeatNo,
      studentInput: record.studentInput,
      studentInputUrl: record.studentInputUrl,
      studentInputOriginUrl: record.studentInputOriginUrl,
      className: record.className,
      // 如果記錄中有 markdown 字段，也包含進去，確保評分標準表格能正常顯示
      ...(record.markdown && { markdown: record.markdown })
    };
    sessionStorage.setItem("workflowResult", JSON.stringify(workflowResult));
    sessionStorage.setItem("uploadMode", record.studentInputUrl !== "" ? "image" : "text");

    // 同章節同篩選(filterRecordsByType 排好序)的學生清單,供批改細節頁「上/下一位」導覽
    // (kanban 106957 R5,比照國中 batchEssaySiblings)。chapterRecords 就是列表當下渲染的
    // 那份 chapter.records,順序/篩選與畫面一致 → 序號 N/M 對得上。缺清單(舊呼叫)時清掉舊值,
    // 避免上一次檢視殘留的清單被誤用。細節頁只在當前筆(_id)命中此清單時才顯示切換。
    if (chapterRecords.length > 0) {
      const siblings = buildFeedbackSiblings(
        chapterRecords,
        selectClass?.className
      );
      sessionStorage.setItem("feedbackSiblings", JSON.stringify(siblings));
    } else {
      sessionStorage.removeItem("feedbackSiblings");
    }
    sessionStorage.setItem("viewMode", "teacherRecord"); // 標記來源為教師查看記錄
    
    // 導向到國文批改專用的 Feedback 頁面
    navigate("/feedback");
  };

  const handleOpenStudentModal = async () => {
    setIsStudentModalOpen(true);
  };

  const handleUpdateStudent = async (
    student: StudentProps,
    updateData: {
      name: string;
      seatNumber: string;
    }
  ) => {
    try {
      await updateStudent(
        student._id,
        selectClass?.classCode || "",
        student.studentID,
        updateData.name,
        student.studentEmail,
        updateData.seatNumber,
        parseInt(updateData.seatNumber)
      );
      
      // 重新獲取學生資料以反映更新
      const updatedStudentsData = await getStudents(selectClass?.classCode || "");
      setStudentsData(updatedStudentsData.data);
      
      // 重新獲取班級記錄
      const classRecords = await getClassRecords(selectClass?.classCode || "", classRecordsBookParam(selectBook));
      setClassRecordsData(classRecords.data);
      
      Swal.fire({
        icon: "success",
        title: "更新成功",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire({
        icon: "error",
        title: "更新失敗",
        timer: 1500,
        showConfirmButton: false,
      });
    }
  };

  // 記錄篩選函數
  const filterRecordsByType = useCallback((records: ClassRecord[]) => {
    if (recordFilter === 'all') {
      // 全部：1. 座號小到大 > 2. 時間舊到新
      return records.sort((a, b) => {
        const seatNoA = parseInt(a.studentSeatNo) || 0;
        const seatNoB = parseInt(b.studentSeatNo) || 0;
        
        if (seatNoA !== seatNoB) {
          return seatNoA - seatNoB; // 座號小到大
        }
        
        // 座號相同時，時間舊到新
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      });
    }

    // 按學生分組
    const studentGroups = records.reduce((groups: { [key: string]: ClassRecord[] }, record) => {
      const studentKey = `${record.studentName}-${record.studentSeatNo}`;
      if (!groups[studentKey]) {
        groups[studentKey] = [];
      }
      groups[studentKey].push(record);
      return groups;
    }, {});

    // 根據篩選類型處理每個學生的記錄
    const filteredRecords: ClassRecord[] = [];
    
    Object.values(studentGroups).forEach((studentRecords) => {
      if (recordFilter === 'best') {
        // 最佳：1. 最高分 > 2. 同分時選時間最新的
        const bestRecord = studentRecords.reduce((best, current) => {
          // 兩種格式都要取得到(kanban 1000517):原本直讀 整體評分.總分,新格式取不到
          // 會被 || '0' 當成 0 分,最佳紀錄靜默選錯 —— 比顯示成 '-' 更難發現。
          const bestScore = parseFloat(extractTotalScore(best.correctingResult) ?? '0');
          const currentScore = parseFloat(extractTotalScore(current.correctingResult) ?? '0');
          
          if (currentScore > bestScore) {
            return current; // 分數更高
          } else if (currentScore === bestScore) {
            // 同分時選時間最新的
            return new Date(current.createdAt) > new Date(best.createdAt) ? current : best;
          } else {
            return best; // 分數較低，保持原來的最佳記錄
          }
        });
        filteredRecords.push(bestRecord);
      } else if (recordFilter === 'first') {
        // 第一次：時間最早的記錄
        const firstRecord = studentRecords.reduce((earliest, current) => {
          return new Date(current.createdAt) < new Date(earliest.createdAt) ? current : earliest;
        });
        filteredRecords.push(firstRecord);
      }
    });

    // 對篩選後的記錄也進行排序（按座號）
    return filteredRecords.sort((a, b) => {
      const seatNoA = parseInt(a.studentSeatNo) || 0;
      const seatNoB = parseInt(b.studentSeatNo) || 0;
      return seatNoA - seatNoB;
    });
  }, [recordFilter]);

  const tabsWidthListener = useCallback(() => {
    if (!tabRef.current || !btnRef.current || !mainContainerRef.current) return;
    if (originTabsWidth === 0) setOriginTabsWidth(tabRef.current.offsetWidth);
    const btnWidth = btnRef.current.offsetWidth;
    const mainContainerWidth = mainContainerRef.current.offsetWidth;
    if (originTabsWidth + btnWidth + 20 > mainContainerWidth) {
      setIsShowTabs(false);
    } else {
      setIsShowTabs(true);
    }
  }, [tabRef, btnRef, mainContainerRef, originTabsWidth]);

  useEffect(() => {
    const fetchAllDatas = async () => {
      setIsInitialLoading(true);
      try {
        const books = await getBooks();
        const classes = await getTeacherClasses(sub);
        
        setAllClassData(classes.data);
        
        // 嘗試恢復之前保存的狀態
        const savedStateString = sessionStorage.getItem("teacherState");
        let savedState = null;
        if (savedStateString) {
          try {
            savedState = JSON.parse(savedStateString);
          } catch (e) {
            console.warn("無法解析保存的教師狀態:", e);
          }
        }
        
        // 檢查是否有班級資料和書籍資料
        if (books.data && books.data.length > 0) {
          // 優先使用保存的狀態，否則使用篩選參數，最後使用預設值
          const targetBookId = savedState?.selectBook || filterBookId || books.data[0]._id;
          setSelectBook(targetBookId);
          
          if (classes.data && classes.data.length > 0) {
            // 找到要選擇的班級
            let targetClass;
            if (savedState?.selectedClass) {
              // 恢復保存的班級
              targetClass = classes.data.find((cls: classDataProps) => 
                cls.classCode === savedState.selectedClass.classCode
              );
            }
            // 老師查詢連結(帶 chapterId)且沒有還原的班級時,優先落在「有這題紀錄的班」
            // (kanban 1001506):8/28 紀錄搬到新書、班級留在舊書後,「第一個班」常常
            // 沒有這題的紀錄,老師開連結看到空列表以為紀錄不見。挑到的班連紀錄一起
            // 拿回來直接用,不再撈第二次;全部沒有 → 照舊落回第一個班。
            let pickedRecords: ClassRecord[] | null = null;
            let chapters: { data: Chapter[] } | null = null;
            if (!targetClass && filterChapterId) {
              chapters = await getChapters(targetBookId, sub);
              const targetChapter = chapters.data?.find((ch: Chapter) => ch._id === filterChapterId);
              if (targetChapter) {
                const picked = await pickClassWithChapterRecords<ClassRecord>(
                  classesForBook(classes.data, targetBookId),
                  targetChapter.chapter,
                  (classCode) =>
                    getClassRecords(classCode, classRecordsBookParam(targetBookId)).then((res) => res.data),
                );
                if (picked) {
                  targetClass = picked.cls;
                  pickedRecords = picked.records;
                }
              }
            }
            if (!targetClass) {
              // 找到屬於目標書籍的第一個班級(學測/模考專區沿用陪練國寫班級)
              targetClass = classes.data.find((cls: classDataProps) => classBelongsToBook(cls, targetBookId));
            }

            if (targetClass) {
              // 批改中/失敗列平行拉,不 await —— 不能拖慢 classrecord 主載入(kanban 106957)
              loadBatchResults(targetClass);
              // 章節要取目標書的(專區班級掛在舊書下,不能用 targetClass.bookID)
              const classRecordsData =
                pickedRecords ??
                (await getClassRecords(targetClass.classCode, classRecordsBookParam(targetBookId))).data;
              if (!chapters) {
                chapters = await getChapters(targetBookId, sub);
              }

              setChaptersData(chapters.data);
              setSelectClass(targetClass);
              setClassRecordsData(classRecordsData);
              
              // 恢復章節選擇和篩選狀態
              if (savedState?.selectedChapter) {
                setSelectedChapter(savedState.selectedChapter);
              } else if (filterChapterId && chapters.data) {
                const targetChapter = chapters.data.find((ch: Chapter) => ch._id === filterChapterId);
                if (targetChapter) {
                  setSelectedChapter(filterChapterId);
                }
              }
              
              // 恢復記錄篩選狀態
              if (savedState?.recordFilter) {
                setRecordFilter(savedState.recordFilter);
              }
            }
          }
        }
      } catch (error) {
        console.error("載入資料失敗:", error);
      } finally {
        // 清除已使用的保存狀態:搬進 finally——原本在 try 區塊內,getBooks()/getTeacherClasses()
        // 拋錯時會跳過 catch 直接沒清到,殘留到下次進頁面繼續污染(kanban 106957 code review)。
        // removeItem 對不存在的 key 是 no-op,不需要再判斷 savedState 是否有值。
        sessionStorage.removeItem("teacherState");
        setIsInitialLoading(false);
      }
    };
    fetchAllDatas();
  }, [sub, loadBatchResults]);
  useEffect(() => {
    const filterClassData: classDataProps[] = classesForBook(allClassData, selectBook);
    setClassData(filterClassData);

    // 自動選擇第一個班級並載入其記錄
    if (filterClassData.length > 0 && (!selectClass || !classBelongsToBook(selectClass, selectBook))) {
      const firstClass = filterClassData[0];
      setSelectClass(firstClass);

      // 切書自動換班:批改中列同步換成新班的(不 await;seq+cleanup 保證不殘留,B6)
      setBatchResults([]);
      loadBatchResults(firstClass);

      // 載入第一個班級的批改記錄
      setIsRecordsLoading(true);
      getClassRecords(firstClass.classCode, classRecordsBookParam(selectBook))
        .then((res) => {
          // 回應回來時選中的班已經不是這個(例如深連結挑班、或 sessionStorage 還原
          // 在這趟請求發出後才把班換掉,kanban 1001506),就丟掉,不要把別班的紀錄蓋上去。
          if (selectClassRef.current?.classCode !== firstClass.classCode) return;
          setClassRecordsData(res.data);
        })
        .catch((error) => {
          console.error("載入班級記錄失敗:", error);
          setClassRecordsData([]);
        })
        .finally(() => {
          setIsRecordsLoading(false);
        });
    }
    
    // 只有當 selectBook 有值時才調用 API
    if (selectBook) {
      getChapters(selectBook, sub)
        .then((res) => {
          setChaptersData(res.data);
        })
        .catch(() => {
        });
    }
  }, [selectBook, allClassData, sub, selectClass, loadBatchResults]);
  
  useEffect(() => {
    // 根據選擇的章節進行篩選
    let chapterMap: string[] = [];
    
    if (selectedChapter) {
      // 如果選擇了特定章節，只顯示該章節
      const selectedChapterData = chaptersData.find(ch => ch._id === selectedChapter);
      if (selectedChapterData) {
        chapterMap = [selectedChapterData.chapter];
      }
    } else if (filterChapterName) {
      // 如果有 URL 篩選參數，使用該章節
      chapterMap = [filterChapterName];
    } else {
      // 否則顯示所有章節
      chapterMap = chaptersData.map((chapter) => chapter.chapter);
    }
    
    const finalRecordsData: FinalRecordsDataProps[] = chapterMap.map(
      (chapterName) => {
        // 章節配對照舊;解析/歸類抽到 splitRecordsByParseError(kanban 1000412)。
        // 解析成功筆行為完全不變(仍就地把字串轉成物件);失敗筆不再整筆濾掉
        // 無聲消失,標 parseError 後以「批改失敗」列顯示 —— correctingResult
        // 保留原始字串,console 照舊留完整內容供追查。
        const { parsed, broken } = splitRecordsByParseError(
          (classRecordsData as ClassRecord[]).filter(
            (record: ClassRecord) => record.chapterID === chapterName
          )
        );
        broken.forEach((record) => {
          console.error("🔴 correctingResult 解析失敗,列表將顯示「批改失敗」:", {
            學生姓名: record.studentName,
            座號: record.studentSeatNo,
            記錄ID: record._id,
            班級代碼: selectClass?.classCode,
            班級名稱: selectClass?.className,
            書籍ID: record.bookID,
            章節ID: record.chapterID,
            建立時間: record.createdAt,
            損壞的資料內容: record.correctingResult,
          });
        });

        // 應用篩選邏輯(最佳/第一次的分組計算只吃解析成功筆,parseError 不參與)
        const filteredRecords = filterRecordsByType(parsed);

        return {
          chapterName,
          records: filteredRecords,
          // 批改失敗(壞 JSON)列固定照「座號小到大 > 時間舊到新」排(同所有記錄的排序),
          // 渲染時接在正常列後面
          brokenRecords: broken.sort((a, b) => {
            const seatNoA = parseInt(a.studentSeatNo) || 0;
            const seatNoB = parseInt(b.studentSeatNo) || 0;
            if (seatNoA !== seatNoB) return seatNoA - seatNoB;
            return (
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
            );
          }),
        };
      }
    );
    setFinalRecordsData(finalRecordsData);
  }, [classRecordsData, chaptersData, selectedChapter, filterChapterName, filterRecordsByType]);
  
  useEffect(() => {
    tabsWidthListener();
    window.addEventListener("resize", tabsWidthListener);
    return () => {
      window.removeEventListener("resize", tabsWidthListener);
    };
  }, [tabsWidthListener, classData]);
  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <Header rwdSizeLg={true} />
      <main className="flex-1 lg:px-xl px-sm flex flex-col pb-sm max-w-2xl w-full mx-auto">
        <div className="flex items-center py-4">
          <BreadCrumb breadCrumb={"/teacher/correcting-records"} />
        </div>
        <hr className="text-black/20" />
        <div className="py-4 flex md:flex-row flex-col justify-between gap-4">
          <TextField
            select
            fullWidth
            label={isInitialLoading ? "載入中..." : "選擇題目"}
            disabled={isInitialLoading}
            sx={{
              maxWidth: "400px",
              minWidth: "150px",
              backgroundColor: "white",
              "@media (max-width: 768px)": {
                maxWidth: "unset",
              },
            }}
            value={selectedChapter}
            onChange={(e) => {
              // 防止選擇空值選項
              if (e.target.value !== "") {
                setSelectedChapter(e.target.value);
              }
            }}
          >
            <MenuItem value="" disabled>
              <em>請選擇題目</em>
            </MenuItem>
            {chapterOptions.map((chapter: Chapter) => (
              <MenuItem key={chapter._id} value={chapter._id}>
                {chapter.chapter}
              </MenuItem>
            ))}
          </TextField>
        </div>
        {isInitialLoading ? (
          <div className="flex-1 flex flex-col items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-3"></div>
            <span className="text-gray-500 text-lg">載入資料中...</span>
          </div>
        ) : (
        <div className="flex flex-col flex-1 relative">
          <div className="flex justify-between items-center gap-x-4 md:flex-row flex-col">
            {!isShowTabs && classData.length > 0 && (
              <TextField
                fullWidth
                value={selectClass?._id}
                sx={{
                  bgcolor: "white",
                  maxWidth: "400px",
                  marginBottom: "16px",
                  "@media (max-width: 768px)": {
                    maxWidth: "unset",
                  },
                }}
                select
              >
                {classData.map((item: classDataProps) => (
                  <MenuItem
                    onClick={() => handleClassSwitch(item)}
                    key={item._id}
                    value={item._id}
                  >
                    {item.className}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </div>
          <div
            ref={tabRef}
            className={`${isShowTabs && classData.length > 0 ? "h-9 mt-4 w-fit" : "hidden"} `}
          >
            {classData.length > 0 &&
              classData.map((item: classDataProps) => {
                return (
                  <span
                    key={item._id + "_" + item.classCode}
                    onClick={() => handleClassSwitch(item)}
                    className={`py-2 px-4 w-fit rounded-t-base bg-white cursor-pointer relative top-2 mr-1 ${
                      selectClass?.classCode === item.classCode
                        ? "text-primary-light py-3 px-6"
                        : "text-black/25"
                    }`}
                  >
                    {item.className}
                  </span>
                );
              })}
          </div>
          <div
            ref={mainContainerRef}
            className="rounded-b-sm bg-white justify-start items-center flex rounded-tr-sm drop-shadow-2xl flex-col gap-y-6 md:gap-y-8 p-4 md:p-10"
          >
            {finalRecordsData && classData && classData.length > 0 ? (
              <>
                {/* 班級資料 - 始終顯示；下載鈕靠右同列(kanban 107190 設計稿) */}
                <div className="w-full">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                      <span className="text-gray-700 font-medium shrink-0">班級資料：</span>
                      <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={handleSetting}
                        variant="outlined"
                        size="small"
                        sx={{
                          textWrap: "nowrap",
                          backgroundColor: "white",
                          color: "#1976d2",
                          borderColor: "#1976d2",
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                            borderColor: "#1976d2"
                          }
                        }}
                      >
                        班級設定
                      </Button>
                      <Button
                        onClick={handleCopy}
                        variant="outlined"
                        size="small"
                        sx={{
                          textWrap: "nowrap",
                          backgroundColor: "white",
                          color: "#1976d2",
                          borderColor: "#1976d2",
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                            borderColor: "#1976d2"
                          }
                        }}
                      >
                        課程邀請碼：{selectClass?.classCode}
                      </Button>
                      </div>
                    </div>
                    {singleDownloadTarget &&
                      renderDownloadButtons(
                        singleDownloadTarget.chapterName,
                        singleDownloadTarget.records
                      )}
                  </div>
                </div>
                
                {/* 作文題目和記錄篩選器 - 始終顯示 */}
                <div className="w-full">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    {/* 作文題目（左邊） */}
                    <div className="flex items-start sm:items-center gap-2 flex-wrap">
                      <span className="text-gray-700 font-medium shrink-0">作文題目：</span>
                      {selectedChapter && chaptersData.length > 0 && (
                        <button
                          onClick={() => setShowQuestionPopup(true)}
                          className="text-primary-light text-base md:text-lg font-medium underline text-left break-words"
                        >
                          {chaptersData.find(ch => ch._id === selectedChapter)?.chapter || "請選擇題目"}
                        </button>
                      )}
                      {!selectedChapter && (
                        <span className="text-gray-500">請選擇題目</span>
                      )}
                    </div>

                    {/* 記錄篩選器（右邊） */}
                    <div className="flex flex-wrap gap-x-4 gap-y-2 sm:gap-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="recordFilter"
                          value="all"
                          checked={recordFilter === 'all'}
                          onChange={(e) => setRecordFilter(e.target.value as 'all' | 'best' | 'first')}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-gray-700 text-sm sm:text-base">所有記錄</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="recordFilter"
                          value="best"
                          checked={recordFilter === 'best'}
                          onChange={(e) => setRecordFilter(e.target.value as 'all' | 'best' | 'first')}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-gray-700 text-sm sm:text-base">最佳記錄</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="recordFilter"
                          value="first"
                          checked={recordFilter === 'first'}
                          onChange={(e) => setRecordFilter(e.target.value as 'all' | 'best' | 'first')}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-gray-700 text-sm sm:text-base">第一次作答記錄</span>
                      </label>
                    </div>
                  </div>
                </div>
                
                {isRecordsLoading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-2"></div>
                    <span className="text-gray-500">載入批改紀錄中...</span>
                  </div>
                ) : finalRecordsData.length > 0 && finalRecordsData.some(chapter => chapter.records.length > 0 || pendingRowsFor(chapter.chapterName).length > 0 || brokenRowsFor(chapter).length > 0) ? (
                  <>
                  {finalRecordsData.map(
                  (chapter: FinalRecordsDataProps) => {
                    // 批次批改中/失敗列(獨立資料源,僅「所有記錄」模式,D1);
                    // 題目剛送批次、尚無任何完成紀錄時也要能撐起表格(A4)。
                    // 批改失敗(壞 JSON)列(kanban 1000412)同模式;僅剩壞筆時也要撐起表格
                    const pendingRows = pendingRowsFor(chapter.chapterName);
                    const brokenRows = brokenRowsFor(chapter);
                    if (chapter.records.length > 0 || pendingRows.length > 0 || brokenRows.length > 0) {
                      // 根據第一筆記錄判斷評分方案(純批改中章節尚無紀錄→用預設方案表頭)
                      // 方案判斷走 normalizeCorrection(kanban 1000517):新格式把四構面
                      // 收進 categories 陣列、頂層沒有「題旨發揮」鍵,直接讀會判成舊制
                      // 那組 —— 表頭選錯組,每格再也對不到值(PM 回報「呈現 - - - -」)。
                      const firstResult = chapter.records[0]?.correctingResult;
                      const isScheme25 = isHsScheme25(firstResult);
                      const scoreCategories = isScheme25
                        ? ["題旨發揮", "結構安排", "字句應用", "資料掌握"]
                        : ["立意取材", "結構組織", "遣詞造句", "錯別字、格式與標點符號"];

                      return (
                        <div
                          key={chapter.chapterName}
                          className="flex flex-col w-full gap-y-4"
                        >
                          {/* 未選題時(全書章節各一張表、或 URL 帶 chapterName 返回)
                              各表上方標明題目,否則多張表看起來就是同一份列表重複
                              (kanban 106957)。樣式沿用上方「作文題目:」列;
                              有選題時上方那列已顯示題名,這裡不重複 */}
                          {!selectedChapter && (
                            <div className="flex items-start sm:items-center gap-2 flex-wrap">
                              <span className="text-gray-700 font-medium shrink-0">作文題目：</span>
                              <span className="text-primary-light text-base md:text-lg font-medium break-words">
                                {chapter.chapterName}
                              </span>
                            </div>
                          )}
                          {!singleDownloadTarget && chapter.records.length > 0 && (
                            <div className="flex justify-end">
                              {renderDownloadButtons(
                                chapter.chapterName,
                                chapter.records
                              )}
                            </div>
                          )}
                          <div className="w-full overflow-x-auto">
                          <table className="w-full bg-gray-100 rounded-lg shadow-md overflow-hidden">
                            <thead className="bg-primary-light lg:text-lg md:text-base text-sm text-nowrap">
                              <tr>
                                <th className="lg:px-4 md:px-2 px-1 py-2 text-white">
                                  座號
                                </th>
                                <th className="lg:px-4 md:px-2 px-1 py-2 text-white">
                                  姓名
                                </th>
                                {scoreCategories.map((cat) => (
                                  <th key={cat} className="lg:px-4 md:px-2 px-1 py-2 text-white md:table-cell hidden">
                                    {cat}
                                  </th>
                                ))}
                                <th className="lg:px-4 md:px-2 px-1 py-2 text-white">
                                  總分
                                </th>
                                <th className="lg:px-4 md:px-2 px-1 py-2 text-white">
                                  上傳時間
                                </th>
                                <th className="lg:px-4 md:px-2 px-1 py-2 text-white">
                                  批改細節
                                </th>
                              </tr>
                            </thead>

                            <tbody className="md:text-base text-sm">
                              {chapter.records.map((record: ClassRecord) => {
                                return (
                                  <tr
                                    key={record._id + "_" + record.createdAt}
                                    className="text-center text-nowrap hover:bg-gray-50"
                                  >
                                    <td>{record.studentSeatNo}</td>
                                    <td>{record.studentName}</td>
                                    {scoreCategories.map((cat) => (
                                      <td key={cat} className="md:table-cell hidden">
                                        {/* 取不到才顯示 '-';離題卷的 0 是真的 0 分,
                                            用 ?? 不能用 || (kanban 1000517) */}
                                        {categoryScore(record.correctingResult, cat) ?? '-'}
                                      </td>
                                    ))}
                                    <td>
                                      {extractTotalScore(record.correctingResult) ?? '-'}
                                    </td>
                                    <td>
                                      <div className="flex flex-wrap gap-x-1 justify-center py-1">
                                        <p>
                                          {
                                            timeProcess(record.createdAt).split(
                                              " "
                                            )[0]
                                          }
                                        </p>
                                        <p>
                                          {
                                            timeProcess(record.createdAt).split(
                                              " "
                                            )[1]
                                          }
                                        </p>
                                      </div>
                                    </td>
                                    <td>
                                      {/* 刪除鈕給所有「已完成」的 classrecord,不分來源(kanban 106957 第 1 項):
                                          老師批次上傳、學生自行上傳、老師本人自行上傳都給刪 ——
                                          後端已放寬成「只驗班導、不排除本人」。批改中的暫時列走
                                          下方 batch-results 的「移除」,不在這裡。樣式沿用批次列 */}
                                      <div className="flex flex-wrap items-center justify-center gap-1.5 py-1">
                                        <Button
                                          sx={{
                                            fontSize: "16px",
                                            padding: "2px 8px",
                                            border: "none",
                                          }}
                                          variant="outlined"
                                          color="primary"
                                          startIcon={<DescriptionIcon />}
                                          onClick={() => handleViewRecord(record, chapter.records)}
                                        >
                                          <span className="lg:inline hidden">
                                            檢視紀錄
                                          </span>
                                        </Button>
                                        {canDeleteClassRecord(record) && (
                                          <button
                                            className="text-xs text-red-600 border border-red-200 px-2.5 py-1 rounded hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                            disabled={deletingRecordId !== null}
                                            onClick={() => handleDeleteClassRecord(record)}
                                          >
                                            {deletingRecordId === record._id
                                              ? "處理中…"
                                              : "刪除"}
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                              {/* 批改失敗列(kanban 1000412):correctingResult 是壞 JSON 的
                                  完成紀錄,不再無聲濾掉。分數欄一律 '-';批改細節欄放
                                  「批改失敗」徽章+操作鈕。不給「檢視紀錄」——
                                  handleViewRecord 會對字串再 JSON.stringify 造成雙重
                                  序列化污染,細節頁也解不開;也不進 feedbackSiblings
                                  (不在 chapter.records 內,建清單時自然排除) */}
                              {brokenRows.map((record: ClassRecord) => {
                                const isRegrading =
                                  !!record._id && regradingIds.has(record._id);
                                return (
                                  <tr
                                    key={record._id + "_" + record.createdAt}
                                    className="text-center text-nowrap hover:bg-gray-50"
                                  >
                                    <td>{record.studentSeatNo}</td>
                                    <td>{record.studentName}</td>
                                    {scoreCategories.map((cat) => (
                                      <td key={cat} className="md:table-cell hidden">
                                        -
                                      </td>
                                    ))}
                                    <td>-</td>
                                    <td>
                                      <div className="flex flex-wrap gap-x-1 justify-center py-1">
                                        <p>{timeProcess(record.createdAt).split(" ")[0]}</p>
                                        <p>{timeProcess(record.createdAt).split(" ")[1]}</p>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="flex flex-wrap items-center justify-center gap-1.5 py-1">
                                        {renderParseErrorBadge(isRegrading)}
                                        {/* 重新批改僅批次來源(canRegradeBrokenRecord):
                                            _id=batchResultID 才有 API 可打;學生自行
                                            上傳維持只給刪除。批改中一律禁用兩鈕 */}
                                        {canRegradeBrokenRecord(record) && (
                                          <button
                                            className="text-xs text-amber-600 border border-amber-300 px-2.5 py-1 rounded hover:bg-amber-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                            disabled={
                                              isRegrading ||
                                              deletingRecordId !== null
                                            }
                                            onClick={() =>
                                              handleRegradeBrokenRecord(record)
                                            }
                                          >
                                            重新批改
                                          </button>
                                        )}
                                        {/* 刪除沿用既有分流(classRecordDeleteSteps):
                                            批次/自行上傳皆可刪,刪掉才能重新上傳批改 */}
                                        {canDeleteClassRecord(record) && (
                                          <button
                                            className="text-xs text-red-600 border border-red-200 px-2.5 py-1 rounded hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                            disabled={
                                              isRegrading ||
                                              deletingRecordId !== null
                                            }
                                            onClick={() => handleDeleteClassRecord(record)}
                                          >
                                            {deletingRecordId === record._id
                                              ? "處理中…"
                                              : "刪除"}
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                              {/* 批次批改中/失敗列:資料來自 batch-results,與上方 classrecord
                                  紀錄並存不去重(B5)。分數欄整段蓋掉 —— 手機蓋總分 1 欄、
                                  桌機 colSpan 蓋構面+總分,對齊表頭的 responsive 欄位 */}
                              {pendingRows.map((row) => (
                                <tr
                                  key={"batch_" + row.key}
                                  className="text-center text-nowrap"
                                >
                                  <td>{row.seatNo}</td>
                                  <td>{row.userName}</td>
                                  <td className="md:hidden py-2">
                                    {renderBatchStatusBadge(row)}
                                  </td>
                                  <td
                                    colSpan={scoreCategories.length + 1}
                                    className="md:table-cell hidden py-2"
                                  >
                                    {renderBatchStatusBadge(row)}
                                  </td>
                                  <td>
                                    {row.createdAt ? (
                                      <div className="flex flex-wrap gap-x-1 justify-center py-1">
                                        <p>{timeProcess(row.createdAt).split(" ")[0]}</p>
                                        <p>{timeProcess(row.createdAt).split(" ")[1]}</p>
                                      </div>
                                    ) : (
                                      "-"
                                    )}
                                  </td>
                                  {/* 檢視鈕一律不給。failed:重新批改+移除;
                                      prohibited:僅移除;批改中:不給任何操作
                                      (樣式仿國中 batch-records 操作欄) */}
                                  <td>
                                    {row.status !== "grading" && row.resultID ? (
                                      <div className="flex flex-wrap items-center justify-center gap-1.5 py-1">
                                        {row.status === "failed" && (
                                          <button
                                            className="text-xs text-amber-600 border border-amber-300 px-2.5 py-1 rounded hover:bg-amber-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                            disabled={reopeningId !== null || removingId !== null}
                                            onClick={() => handleReopenBatchResult(row)}
                                          >
                                            {reopeningId === row.resultID ? "處理中…" : "重新批改"}
                                          </button>
                                        )}
                                        <button
                                          className="text-xs text-red-600 border border-red-200 px-2.5 py-1 rounded hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                          disabled={removingId !== null || reopeningId !== null}
                                          onClick={() => handleRemoveBatchResult(row)}
                                        >
                                          {removingId === row.resultID ? "處理中…" : "移除"}
                                        </button>
                                      </div>
                                    ) : (
                                      <span className="text-gray-400">—</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </div>
                        </div>
                      );
                    }
                  }
                )}
                  </>
                ) : (
                  <p className="text-xl tracking-wide text-black/40 select-none">
                    此班級尚未有批改紀錄
                  </p>
                )}
              </>
            ) : (
              <p className="text-xl tracking-wide text-black/40 select-none">
                尚未啟用此書的教師開通碼，如需協助，請聯繫翰林業務或經銷夥伴
              </p>
            )}
          </div>
        </div>
        )}
      </main>
      <Footer />
      <ClassSettingModal
        open={isClassSetOpen}
        onClose={() => setIsClassSetOpen(false)}
        className={selectClass?.className}
        handleClassNameChange={handleClassNameChange}
        handleOpenStudentModal={handleOpenStudentModal}
        canEditStudent={canEditStudent}
        handleDeleteClass={handleDeleteClass}
      />
      <CopyModal
        open={isCopyOpen}
        onClose={() => setIsCopyOpen(false)}
        classCode={selectClass?.classCode || ""}
      />
      <StudentSettingModal
        open={isStudentModalOpen}
        onClose={() => setIsStudentModalOpen(false)}
        studentsData={studentsData}
        selectClass={selectClass}
        handleUpdateStudent={handleUpdateStudent}
      />
      
      {/* 題目內容 Popup */}
      {showQuestionPopup && selectedChapter && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold text-gray-800">
                {chaptersData.find(ch => ch._id === selectedChapter)?.chapter || "題目"}
              </h2>
              <button
                onClick={() => setShowQuestionPopup(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            
            {/* 題目內容 */}
            {(() => {
              const selectedChapterData = chaptersData.find(ch => ch._id === selectedChapter);
              if (!selectedChapterData?.chapterDesc) return null;

              return (
                <div className="mb-6">
                  <h3 className="font-semibold mb-3">題目內容</h3>
                  <div className="text-gray-700 text-sm leading-relaxed">
                    <TopicDesc
                      desc={selectedChapterData.chapterDesc}
                      images={selectedChapterData.images}
                      chapterName={selectedChapterData.chapter}
                    />
                  </div>

                  {/* 顯示章節圖片 */}
                  {selectedChapterData.chapterDescUrl && (
                    <div className="mt-4">
                      <img
                        src={selectedChapterData.chapterDescUrl}
                        alt={`${selectedChapterData.chapter} - 章節圖片`}
                        className="w-full max-w-2xl mx-auto rounded-md border border-gray-200 shadow-sm"
                        loading="lazy"
                        onError={(e) => {
                          console.warn('章節圖片載入失敗:', selectedChapterData.chapterDescUrl);
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })()}
            
            <div className="flex justify-end">
              <button
                onClick={() => setShowQuestionPopup(false)}
                className="px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
              >
                關閉
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 匯出為預設 - 符合新專案的慣例
export default CorrectingRecords;